import { clsx } from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type IconPillSize = 24 | 32 | 36 | 48 | 56;

export type IconPillTone =
  | "gradient"
  | "light"
  | "ghost"
  | "ghostMuted"
  | "outline"
  | "dark"
  | "cyanTint";

export type IconPillShape = "square" | "circle";

type BaseProps = {
  size?: IconPillSize;
  tone?: IconPillTone;
  shape?: IconPillShape;
  className?: string;
  children: ReactNode;
};

export type IconPillProps =
  | (BaseProps & { as?: "div"; onClick?: never })
  | (BaseProps & {
      as: "button";
      onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
      type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
      disabled?: boolean;
      "aria-label"?: string;
    });

const SIZE_CLASS: Record<IconPillSize, string> = {
  24: "size-6",
  32: "size-8",
  36: "size-9",
  48: "size-12",
  56: "size-14",
};

const SQUARE_RADIUS: Record<IconPillSize, string> = {
  24: "rounded-md",
  32: "rounded-md",
  36: "rounded-[9px]",
  48: "rounded-[10px]",
  56: "rounded-[14px]",
};

const TONE_CLASS: Record<IconPillTone, string> = {
  gradient:   "bg-gradientGreen text-textOnDark",
  light:      "bg-borderLight text-textDark",
  ghost:      "bg-bgLight text-textDark",
  ghostMuted: "bg-bgLight text-textMuted",
  outline:    "bg-bgWhite border border-borderDefault text-textDark",
  dark:       "bg-brandBlack text-textOnDark",
  cyanTint:   "bg-bgLight text-accentCyan",
};

export function IconPill(props: IconPillProps) {
  const {
    size = 36,
    tone = "light",
    shape = "square",
    className,
    children,
  } = props;

  const classes = clsx(
    "inline-flex items-center justify-center shrink-0 transition-colors",
    SIZE_CLASS[size],
    shape === "circle" ? "rounded-full" : SQUARE_RADIUS[size],
    TONE_CLASS[tone],
    className,
  );

  if (props.as === "button") {
    const { onClick, type = "button", disabled, "aria-label": ariaLabel } = props;
    return (
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        className={clsx(
          classes,
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-accentCyan",
          disabled && "cursor-not-allowed opacity-50",
          !disabled && "hover:brightness-95 active:brightness-90",
        )}
      >
        {children}
      </button>
    );
  }

  return <span className={classes}>{children}</span>;
}
